'use client';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, Check, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ComposerSkillCoverMedia } from '@/components/visual-editor/chat/ComposerSkillCoverMedia';
import { CreationGlassSurface } from '@/components/visual-editor/CreationGlassSurface';
import styles from './OnboardingSurface.module.css';
import { GuideOrbButton } from '@/components/ui/GuideOrbButton';
import {
  CONVERSATION_CAPABILITY_MENU_BY_FAMILY,
  CONVERSATION_TASK_FAMILY_DEFINITIONS,
  FEATURED_CONVERSATION_TASK_FAMILIES,
  type ConversationCapabilityKey,
} from '@/features/conversation-capabilities/contract';
import { TaskFamilyIconTile } from '@/features/conversation-capabilities/TaskFamilyIcon';
import { useConversationCapabilityMenu } from '@/features/conversation-capabilities/useConversationCapabilityMenu';
import type { OnboardingCreationModule } from '@/lib/api/users';
import { cn } from '@/lib/cn';
import {
  clearOnboardingStartQueryMarker,
  getOnboardingPreference,
  getOnboardingTaskFamily,
  ONBOARDING_REOPEN_CREATION_GUIDE_EVENT,
} from '@/lib/onboarding';
import { startOnboardingCreation } from '@/lib/onboarding-creation-completion';
import { useUserProfileStore } from '@/stores/userProfileStore';

interface OnboardingCreationGuideProps {
  onStartModule: (
    module: OnboardingCreationModule,
    capability: ConversationCapabilityKey,
  ) => void;
}

export function OnboardingCreationGuide({ onStartModule }: OnboardingCreationGuideProps) {
  const t = useTranslations('onboarding.activation');
  const exploreT = useTranslations('explore');
  const reduceMotion = useReducedMotion();
  const profile = useUserProfileStore((state) => state.profile);
  const onboardingPreference = getOnboardingPreference(profile);
  const activation = onboardingPreference?.activation;
  const capabilityMenu = useConversationCapabilityMenu();
  const [isOpen, setIsOpen] = useState(false);
  const [activeModule, setActiveModule] = useState<OnboardingCreationModule | null>(null);
  const [selectedCapability, setSelectedCapability] = useState<ConversationCapabilityKey | null>(null);
  const [error, setError] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const initialRecommendedLaunchRef = useRef(false);

  const recommendedModule = activation?.recommended_module ?? null;
  const activeFamily = activeModule ? getOnboardingTaskFamily(activeModule) : null;
  const activeCapabilities = useMemo(() => {
    if (!activeFamily) return [];
    return CONVERSATION_CAPABILITY_MENU_BY_FAMILY[activeFamily].flatMap((capability) => {
      const item = capabilityMenu.get(capability);
      return item ? [item] : [];
    });
  }, [activeFamily, capabilityMenu]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setActiveModule(null);
    setSelectedCapability(null);
    setError('');
  }, []);

  const openModule = useCallback((module: OnboardingCreationModule) => {
    setActiveModule(module);
    setSelectedCapability(null);
    setError('');
    window.requestAnimationFrame(() => backButtonRef.current?.focus());
  }, []);

  const startCapability = useCallback(async (
    module: OnboardingCreationModule,
    capability: ConversationCapabilityKey,
  ) => {
    setSelectedCapability(capability);
    setError('');
    setIsOpen(false);
    setActiveModule(null);
    const activationStart = startOnboardingCreation(module, capability);
    onStartModule(module, capability);
    try {
      await activationStart;
    } catch {
      setError(t('saveFailed'));
    }
  }, [onStartModule, t]);

  useEffect(() => {
    if (initialRecommendedLaunchRef.current || typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('onboarding') !== '1') return;
    if (!onboardingPreference) return;
    if (
      onboardingPreference.status === 'skipped'
      || activation?.status === 'completed'
      || activation?.status === 'dismissed'
      || (onboardingPreference.status === 'completed' && !activation)
    ) {
      initialRecommendedLaunchRef.current = true;
      clearOnboardingStartQueryMarker();
      return;
    }
    if (activation?.status !== 'pending' && activation?.status !== 'started') return;

    if (!activation.recommended_module) return;

    initialRecommendedLaunchRef.current = true;
    clearOnboardingStartQueryMarker();
    setIsOpen(true);
  }, [
    activation,
    onboardingPreference,
  ]);

  useEffect(() => {
    const handleReopen = (event: Event) => {
      const creationModule = (event as CustomEvent<{ module?: OnboardingCreationModule }>).detail?.module;
      setIsOpen(true);
      if (creationModule) {
        openModule(creationModule);
        return;
      }
      setActiveModule(null);
      setSelectedCapability(null);
      setError('');
    };
    window.addEventListener(ONBOARDING_REOPEN_CREATION_GUIDE_EVENT, handleReopen);
    return () => window.removeEventListener(ONBOARDING_REOPEN_CREATION_GUIDE_EVENT, handleReopen);
  }, [openModule]);

  useEffect(() => {
    if (isOpen && document.activeElement === launcherRef.current) {
      menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus({ preventScroll: true });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || launcherRef.current?.contains(target)) return;
      closeMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      if (activeModule) {
        setActiveModule(null);
        setSelectedCapability(null);
        return;
      }
      closeMenu();
      launcherRef.current?.focus();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModule, closeMenu, isOpen]);

  return (
    <>
      <AnimatePresence>
        {isOpen ? (
          <motion.div
            ref={menuRef}
            id="first-creation-guide-menu"
            role="menu"
            aria-label={activeModule ? t('sheet.chooseCapability') : t('sheet.title')}
            initial={reduceMotion ? false : { opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: 'right bottom' }}
            onKeyDown={event => {
              if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
              const controls = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
              const index = controls.indexOf(document.activeElement as HTMLButtonElement);
              const next = event.key === "Home" ? 0 : event.key === "End" ? controls.length - 1 : (index + (event.key === "ArrowUp" ? -1 : 1) + controls.length) % controls.length;
              event.preventDefault(); controls[next]?.focus();
            }}
            className="fixed bottom-[96px] right-4 z-[80] w-[min(344px,calc(100vw-32px))] overflow-hidden rounded-[18px] border border-[#DFE5E8] bg-transparent p-0 sm:right-7"
          >
            <CreationGlassSurface className={styles.guide}>
            <AnimatePresence mode="wait" initial={false}>
              {!activeModule ? (
                <motion.div
                  key="task-families"
                  initial={reduceMotion ? false : { opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: reduceMotion ? 0 : 0.16 }}
                  className="space-y-1"
                >
                  <p className="px-2.5 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#8291A4]">
                    {t('sheet.title')}
                  </p>
                  {FEATURED_CONVERSATION_TASK_FAMILIES.map((family) => {
                    const definition = CONVERSATION_TASK_FAMILY_DEFINITIONS[family];
                    const creationModule = definition.onboardingModule;
                    if (!creationModule) return null;
                    const recommended = recommendedModule === creationModule;
                    return (
                      <button
                        key={family}
                        type="button"
                        role="menuitem"
                        onClick={() => openModule(creationModule)}
                        className="group flex min-h-[62px] w-full items-center gap-2.5 rounded-[12px] border border-transparent px-2.5 py-2 text-left transition hover:border-[#DCECF3] hover:bg-[#F2F8FA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4ABDE9]"
                      >
                        <TaskFamilyIconTile family={family} tileClassName="group-hover:text-[#2393BD]" />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-[13px] font-semibold leading-5 text-[#26313A]">
                              {exploreT(definition.labelKey as never)}
                            </span>
                            {recommended ? (
                              <span className="shrink-0 rounded-full bg-[#DDF4FC] px-1.5 py-0.5 text-[9px] font-bold text-[#197B9E]">
                                {t('recommended')}
                              </span>
                            ) : null}
                          </span>
                          <span className="mt-0.5 block truncate text-[11px] text-[#8291A4]">
                            {exploreT(definition.hintKey as never)}
                          </span>
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-[#91A0AE]" strokeWidth={1.8} aria-hidden />
                      </button>
                    );
                  })}
                </motion.div>
              ) : (
                <motion.div
                  key={`capabilities-${activeModule}`}
                  initial={reduceMotion ? false : { opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: reduceMotion ? 0 : 0.16 }}
                >
                  <div className="flex items-center gap-2 px-1 pb-2">
                    <button
                      ref={backButtonRef}
                      type="button"
                      onClick={() => {
                        setActiveModule(null);
                        setSelectedCapability(null);
                      }}
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[9px] text-[#637688] transition hover:bg-[#EEF5F8] hover:text-[#26374B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]"
                      aria-label={t('sheet.back')}
                    >
                      <ArrowLeft className="h-4 w-4" strokeWidth={1.8} aria-hidden />
                    </button>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-semibold text-[#26313A]">
                        {exploreT(CONVERSATION_TASK_FAMILY_DEFINITIONS[activeFamily!].labelKey as never)}
                      </p>
                      <p className="truncate text-[10px] text-[#8291A4]">{t('sheet.chooseCapability')}</p>
                    </div>
                  </div>
                  <div className="max-h-[min(410px,calc(100vh-180px))] space-y-1 overflow-y-auto overscroll-contain pr-0.5">
                    {activeCapabilities.map((capability) => {
                      const selected = selectedCapability === capability.key;
                      return (
                        <button
                          key={capability.key}
                          type="button"
                          role="menuitemradio"
                          onClick={() => void startCapability(activeModule, capability.key)}
                          className={cn(
                            'group flex min-h-[58px] w-full items-center gap-2.5 rounded-[12px] border px-2.5 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4ABDE9]',
                            selected
                              ? 'border-[#8BD9F1] bg-[#EAF8FD]'
                              : 'border-transparent hover:border-[#DCECF3] hover:bg-[#F2F8FA]',
                          )}
                          aria-checked={selected}
                        >
                          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[9px] bg-[#EFF4F7] text-[#668092]">
                            <ComposerSkillCoverMedia
                              src={capability.previewImage}
                              alt=""
                              className="h-full w-full"
                              mediaClassName="h-full w-full object-cover"
                            />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-semibold text-[#26313A]">{capability.title}</span>
                            <span className="mt-0.5 block truncate text-[11px] text-[#8291A4]">{capability.hint}</span>
                          </span>
                          {selected ? (
                            <Check className="h-4 w-4 shrink-0 text-[#1686AF]" strokeWidth={2.5} aria-hidden />
                          ) : (
                            <ChevronRight className="h-4 w-4 shrink-0 text-[#91A0AE]" strokeWidth={1.8} aria-hidden />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {error ? (
              <p role="alert" className="px-2 pb-1 pt-2 text-xs font-medium leading-4 text-[#B42318]">{error}</p>
            ) : null}
            </CreationGlassSurface>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <GuideOrbButton
        ref={launcherRef}
        onClick={() => {
          if (isOpen) {
            closeMenu();
            return;
          }
          setError('');
          setActiveModule(null);
          setSelectedCapability(null);
          setIsOpen(true);
        }}
        className="fixed bottom-[22px] right-4 z-[82] inline-flex h-16 w-16 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-4 sm:right-7"
        ariaLabel={t('launcherAria')}
        active={isOpen}
        hasPopup="menu"
        controls="first-creation-guide-menu"
      />
    </>
  );
}
